<template>
  <div>
    <div
      class="container">
      <grid-row
        column-align="center">
        <grid-col
          col-size="4">
          <div
            class="logo-wrapper">
            <login-logo-mobile class="logo" />
          </div>
          <div
            class="title-wrapper">
            <h1
              class="header margin-top-xl">
              {{ $t('resetPassword.title') }}
            </h1>
          </div>
        </grid-col>
      </grid-row>
      <grid-row
        v-if="showSuccess"
        column-align="center">
        <grid-row
          col-size="4">
          <h2>{{ $t('signin.success') }}</h2>
        </grid-row>
      </grid-row>
      <grid-row
        v-if="showError"
        column-align="center">
        <grid-row
          col-size="4">
          <h2>{{ $t('signin.error') }}</h2>
        </grid-row>
      </grid-row>
      <form
        v-if="!showSuccess && !showError"
        v-on:submit.prevent
        v-on:keypress.enter.prevent="onSave"
        v-on:keydown.space.prevent="'false'">
        <grid-row column-align="center">
          <grid-col col-size="4">
              <label-input
                :lowercase="true"
                ref="nameInput"
                form-size="m"
                :label="$t('user.nameLabel')"
                v-model="name"
                :type="'text'"
                :error="nameErrorLenght || nameErrorUnique"
                :errorMessage="displayNameErrorMessage">
              </label-input>
          </grid-col>
        </grid-row>
        <grid-row
          column-align="center">
          <grid-col col-size="4">
              <label-input
                ref="passwordInput"
                form-size="m"
                :label="$t('user.password')"
                v-model="password"
                :type="'password'"
                :error="passwordError"
                :errorMessage="displayPasswordErrorMessage"
                autocomplete="new-password">
              </label-input>
          </grid-col>
        </grid-row>
        <grid-row
          column-align="center">
          <grid-col
            col-size="4">
              <label-input
                ref="passwordRepeatInput"
                form-size="m"
                :label="$t('user.passwordRepeat')"
                v-model="passwordRepeat"
                :type="'password'"
                :error="passwordRepeatError"
                :errorMessage="$t('user.passwordShouldBeEqual')"
                autocomplete="new-password">
              </label-input>
          </grid-col>
        </grid-row>
        <grid-row
          column-align="center">
          <grid-col
            col-size="4">
            <label-input
              v-model="uuid"
              type="hidden">
            </label-input>
          </grid-col>
        </grid-row>
        <grid-row
          column-align="center">
          <grid-col
            col-size="4">
            <div>
              <btn-pill
                class="btn-create"
                :btnText="$t('resetPassword.send')"
                v-on:click="onClick"
                btn-size="m">
              </btn-pill>
            </div>
          </grid-col>
        </grid-row>
      </form>
    </div>
  </div>
</template>

<script>
import {mapActions} from 'vuex';
import LoginLogoMobile from '@/components/shared/logos/LoginLogoMobile';
import MainLayout from '@/layouts/MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import LabelInput from '@/components/shared/forms/LabelInput';
import BtnPill from '@/components/shared/buttons/BtnPill';

export default {
  components: {
    MainLayout,
    LoginLogoMobile,
    GridRow,
    GridCol,
    LabelInput,
    BtnPill
  },
  data () {
    return {
      name: '',
      password: '',
      passwordRepeat: '',
      uuid: this.$route.query.uuid,
      /* Form field errors */
      nameErrorLenght: false,
      nameErrorUnique: false,
      passwordError: false,
      passwordRepeatError: false,
      emailError: false,
      /* Form */
      showSuccess: false,
      showError: false
    };
  },
  mounted () {
    this.$refs.nameInput.focus();
  },
  computed: {
    displayNameErrorMessage () {
      let errorMessage = this.$t('commons.mandatoryFieldMessage');
      if (this.nameErrorUnique) {
        errorMessage = this.$t('user.userAlreadyExists');
      }
      return errorMessage;
    },
    displayPasswordErrorMessage () {
      let errorMessage = this.$t('commons.mandatoryFieldMessage');
      if (this.passwordError) {
        errorMessage = this.$t('user.passwordFormatMessage');
      }
      return errorMessage;
    }
  },
  methods: {
    ...mapActions([
      'resetPasswordForm'
    ]),

    onClick () {
      this.clearErrors(this);

      if (this.isValidForm()) {
        const user = {};
        user.username = this.name.toLowerCase();
        user.new_password = this.password;
        user.token = this.uuid.replace(/['"]+/g, '');

        this.resetPasswordForm({ context: this, user: user }).then(() => {
          this.handlerSuccess();
        }, error => {
          this.handlerError(error);
        });
      }
    },
    handlerSuccess () {
      this.clearErrors();
      this.showSuccess = true;
    },
    handlerError (error) {
      switch (error.status) {
        case 409:
          this.nameErrorUnique = true;
          break;

        case 400:
          if (error.bodyText.includes('password')) {
            this.passwordError = true;
          } else {
            this.emailError = true;
          }
          break;

        default:
          this.showError = true;
          break;
      }
    },
    isValidForm () {
      let valid = true;
      if (!(this.name && this.name.length > 0)) {
        valid = false;
        this.nameErrorLenght = true;
      }
      if (!(this.password && isValidPassword(this.password))) {
        valid = false;
        this.passwordError = true;
      }
      if (!(this.password === this.passwordRepeat)) {
        valid = false;
        this.passwordRepeatError = true;
      }
      if (!this.email && isValidEmail(this.email)) {
        valid = false;
        this.emailError = true;
      }

      return valid;
    },
    clearErrors () {
      this.nameErrorLenght = false;
      this.nameErrorUnique = false;
      this.passwordError = false;
      this.passwordRepeatError = false;
      this.emailError = false;
    }
  }
};

const isValidPassword = (password) => {
  const containsLetters = /[a-z]*[A-Z]*/.test(password);
  const containsNumbers = /\d/.test(password);
  const passwordLength = password.length >= 6;
  return (containsLetters === true && containsNumbers === true && passwordLength === true);
};

const isValidEmail = (email) => {
  const regExp = /^([\w-.]+@([\w-]+\.)+[\w-]{2,4})?$/;
  return regExp.test(String(email).toLowerCase());
};
</script>

<style lang="scss" scoped>
  @import "src/stylesheets/variables";

  .logo-wrapper {
    margin-top: $sp-margin;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  .logo {
    width: 6rem;
    height: auto;
  }

  .title-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  div.license-agreement {
    width: 100%;
    height: 200px;
    overflow :scroll;
    padding: $sp-margin-small;
    margin-bottom: $sp-margin-small;
    border: 1px solid $black-trans6;
    border-radius: $radius;
    overflow-x: hidden
  }

  .btn-create {
    width: 100%;
    margin-bottom: $sp-margin-larger;
  }

  .advice {
    font-size: $fs-xs;
    text-align: center;
  }

</style>



// WEBPACK FOOTER //
// src/layouts/ResetPasswordForm.vue